import { useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { arrayToObject, take } from 'lib/array/arrayUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import {
  getMultiRoomCalendarDays,
  getMultiRoomDaysOtherAvailability,
  getSelectedOfferPageMultiRoomDay,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import { getCalendarDays, getDaysOtherAvailability, getSelectedOfferPageDay } from 'selectors/offerPage/offerPageSelectors'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import OfferCalendarSidebarPriceBlurb from './OfferCalendarSidebarPriceBlurb'
import useMountedEffect from 'hooks/useMountedEffect'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import OfferCalendarSidebarDaysMonth from './OfferCalendarSidebarDaysMonth'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

const Calendar = styled.div`
  animation: ${fadeIn} 0.2s;
`

interface Props {
  daysByMonth: Array<MonthDayView>
  className?: string
  onBackToMonth?: () => void
  initialMonth?: string
  otherAvailability: { [checkIn: string]: boolean }
  selectedDay?: App.CalendarDay
  flightsOnly?: boolean
  contentRef: React.RefObject<HTMLDivElement | null>
  isAnyFlightsActive?: boolean
  bundleOfferId?: string
  roomCount: number
  displayMemberPrice: boolean
}

function OfferCalendarSidebarDayPicker(props: Props) {
  const {
    daysByMonth,
    className,
    initialMonth,
    otherAvailability,
    selectedDay,
    flightsOnly,
    contentRef,
    isAnyFlightsActive,
    onBackToMonth,
    bundleOfferId,
    roomCount,
    displayMemberPrice,
  } = props
  const intl = useIntl()
  // always show month selected, previous month and next month
  const initialMonthIndex = useRef(daysByMonth.findIndex((entry) => entry[1][0]?.monthKey === initialMonth)).current
  const [monthsOnDisplay, setMonthsOnDisplay] = useState({ toShow: 3, offset: Math.max(initialMonthIndex - 1, 0) })
  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)

  // We discard the initial 'previous in view' check because we already show
  // an extra month before this one on mount
  useMountedEffect(() => {
    if (previousInView) {
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  // mounting layout handles transition from month to day calendar
  useEffect(() => {
    if (initialMonthIndex > 0) {
      // we need to scroll to the 'middle' month
      const month = daysByMonth[monthsOnDisplay.offset]
      const element = monthElements.current[month[1][0].monthKey]
      if (element) {
        const scrollPosition = element.offsetHeight + element.offsetTop
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // on offset change handles when scrolling up and new month appears
  useEffect(() => {
    if (scrollRestoreMonth.current !== undefined) {
      const month = daysByMonth[monthsOnDisplay.offset]
      const element = monthElements.current[month[1][0].monthKey]
      if (element) {
        const scrollPosition = element.offsetHeight + element.offsetTop + 40
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextInView) {
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  const monthData = useMemo(() => {
    return arrayToObject(daysByMonth, ([title]) => title, ([_, days]) => {
      const withTotals = days.filter(day => {
        if (flightsOnly && !day.flightCacheActive) {
          // we don't have a price to show if flight cache isn't active and we're only showing flights
          return false
        }
        return isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
      })
      const hotelFromPrice = withTotals.length ? Math.min(...withTotals.map(d => displayMemberPrice ? d.hotelMemberTotal : d.hotelTotal)) : 0
      const packagedFromPrice = withTotals.length ? Math.min(...withTotals.map(d => displayMemberPrice ? d.packagedMemberTotal : d.packagedTotal)) : 0

      return {
        hotelFromPrice,
        packagedFromPrice,
        hasSpecial: days.some(day => (day.dealId || day.hasTactical) && day.hotelAvailable),
      } })
  }, [daysByMonth, displayMemberPrice, flightsOnly, isAnyFlightsActive])

  return (
    <VerticalSpacer className={className} gap={20}>
      {isAnyFlightsActive && <Caption variant="medium" colour="neutral-two">
        {intl.formatMessage(
          {
            id: 'h4SBsWO3Y-Hidjkixk8v',
            defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply',
            description: 'Disclaimer about indicative flight pricing on the calendar sidebar',
          },
          { brandName: config.brandConfig.name },
        )}
      </Caption>}
      {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
      {take(daysByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map(([title, days]) => {
        const fromPrice = isAnyFlightsActive ? monthData[title].packagedFromPrice : monthData[title].hotelFromPrice

        return <Calendar key={title} ref={(el) => { monthElements.current[days[0].monthKey] = el }}>
          <VerticalSpacer gap={8}>
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <Heading variant="heading5">{days[0].month}</Heading>
                <TextLink size="large" onClick={onBackToMonth}>{days[0].year}</TextLink>
              </Group>
              {!!fromPrice && !bundleOfferId && <OfferCalendarSidebarPriceBlurb
                fromPrice={fromPrice}
                roomCount={roomCount}
                special={monthData[title].hasSpecial}
                hasLoyaltyRoomUpgrade={days.some(day => !!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package)}
              />}
            </VerticalSpacer>
            <OfferCalendarSidebarDaysMonth
              days={days}
              cheapestPrice={fromPrice}
              selectedDay={selectedDay}
              otherAvailability={otherAvailability}
            />
          </VerticalSpacer>
        </Calendar>
      })}
      {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < daysByMonth.length && <div ref={nextRef} />}
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    bundleOfferId: state.bundleOffer?.id,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    daysByMonth: isMultiRoom ? getMultiRoomCalendarDays(state, appState) : getCalendarDays(appState, state),
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    isAnyFlightsActive: state.flights,
    roomCount: state.rooms?.length ?? 1,
    displayMemberPrice: currentState.displayMemberPrice,
  }
}, OfferCalendarSidebarDayPicker)
